import { useEffect, useState } from "react";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  IconButton,
  TextField,
  Autocomplete,
  Chip,
} from "@mui/material";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";

import CustomTypography from "../../globalStyles/fonts";
import CloseIcon from "@mui/icons-material/Close";
import { toast } from "react-toastify";
import { usersService } from "../../utils/services/usersService";
import { roleService } from "../../utils/services/role.service";
import ClientAutocomplete from "../clientAutocomplete";
import { fieldSx } from "../../pages/manage-catalog/constants";

const UserModal = ({
  open,
  handleClose,
  onAddData,
  selectedUser,
  client_id,
}) => {
  const [userDetails, setUserDetails] = useState({
    name: "",
    email: "",
    phone_no: "",
    password: "",
    roles: [],
  });
  const [showPassword, setShowPassword] = useState(false);
  const [availableRoles, setAvailableRoles] = useState([]);
  const [loadingRoles, setLoadingRoles] = useState(false);
  const [formErrors, setFormErrors] = useState({});
  const [selectedClient, setSelectedClient] = useState(null);

  const initialData = () => {
    setUserDetails({
      name: "",
      email: "",
      phone_no: "",
      password: "",
      roles: [],
    });
    setFormErrors({});
    setSelectedClient(null);
  };

  // Fetch roles from the API
  const fetchRoles = async () => {
    try {
      setLoadingRoles(true);
      const response = await roleService.fetch();
      if (response && response.data) {
        setAvailableRoles(response.data);
      }
    } catch (error) {
      console.error("Error fetching roles:", error);
      toast.error("Failed to load roles");
    } finally {
      setLoadingRoles(false);
    }
  };

  const isValidEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const isValidPhoneNumber = (number) => {
    const phoneRegex = /^\(?([0-9]{3})\)?[-. ]?([0-9]{3})[-. ]?([0-9]{4})$/;
    return phoneRegex.test(number);
  };

  const validateForm = () => {
    const errors = {};

    if (!userDetails.name) {
      errors.name = "Name is required.";
    }

    if (!userDetails.email) {
      errors.email = "Email is required.";
    } else if (!isValidEmail(userDetails.email)) {
      errors.email = "Enter a valid email.";
    }

    if (!userDetails.phone_no) {
      errors.phone_no = "Phone number is required.";
    } else if (!isValidPhoneNumber(userDetails.phone_no)) {
      errors.phone_no = "Enter a valid phone number.";
    }

    if (!userDetails.roles || userDetails.roles.length === 0) {
      errors.roles = "Kindly select at least one role.";
    }

    if (!selectedUser && !userDetails.password) {
      errors.password = "Password is required.";
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  useEffect(() => {
    if (selectedUser) {
      setUserDetails({
        name: selectedUser?.name || "",
        email: selectedUser?.email || "",
        phone_no: selectedUser?.phone_no || "",
        password: selectedUser?.password || "",
        roles: selectedUser?.roles || [],
      });
    } else {
      initialData();
    }
  }, [selectedUser]);

  useEffect(() => {
    fetchRoles();
  }, []);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setUserDetails((prevData) => ({
      ...prevData,
      [name]: value,
    }));
  };

  const handleRolesChange = (event, newValue) => {
    setUserDetails((prevData) => ({
      ...prevData,
      roles: newValue,
    }));
  };

  const handleEditData = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;
    try {
      const { name, email, phone_no, password, roles } = userDetails;

      await usersService.edit(
        {
          name,
          email,
          phone_no,
          roles: roles.map((role) => role._id), // Send only the role IDs
        },
        selectedUser._id,
      );
      onAddData && onAddData();
      toast.success("User updated successfully!");
      initialData();
      handleClose();
    } catch (e) {
      toast.error("Failed to update user.");
      console.error(e);
    }
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;
    try {
      const userData = {
        ...userDetails,
        roles: userDetails.roles.map((role) => role._id), // Send only the role IDs
      };
      if (client_id || selectedClient) {
        await usersService.createClientUser({
          ...userData,
          client_id: client_id || selectedClient._id,
        });
      } else {
        await usersService.create(userData);
      }
      onAddData && onAddData();
      toast.success("User created successfully!");
      initialData();
      handleClose();
    } catch (e) {
      toast.error("Failed to create user.");
      console.error(e);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={() => {
        initialData();
        handleClose();
      }}
      maxWidth="sm"
      fullWidth
    >
      <div style={{ display: "flex", justifyContent: "space-between" }}>
        <DialogTitle
          style={{ fontFamily: "Poppins", fontSize: 24, fontWeight: 500 }}
        >
          {selectedUser ? "Edit User" : "Create User"}
        </DialogTitle>
        <IconButton onClick={handleClose}>
          <CloseIcon />
        </IconButton>
      </div>
      <Divider style={{ marginBottom: 10 }} />
      <DialogContent>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <CustomTypography>Name</CustomTypography>
            <TextField
              size="small"
              name="name"
              value={userDetails.name}
              onChange={handleInputChange}
              fullWidth
              margin="dense"
              error={!!formErrors.name}
              helperText={formErrors.name}
              sx={fieldSx}
            />
          </Grid>
          <Grid item xs={12}>
            <CustomTypography>Email</CustomTypography>
            <TextField
              size="small"
              name="email"
              value={userDetails.email}
              onChange={handleInputChange}
              fullWidth
              margin="dense"
              error={!!formErrors.email}
              helperText={formErrors.email}
              sx={fieldSx}
            />
          </Grid>
          {selectedUser || client_id ? (
            <></>
          ) : (
            <Grid item xs={12}>
              <CustomTypography>Client</CustomTypography>
              <ClientAutocomplete
                selectedClient={selectedClient}
                onClientChange={setSelectedClient}
                hideLabel
              />
            </Grid>
          )}
          <Grid item xs={12}>
            <CustomTypography>Phone Number</CustomTypography>
            <TextField
              size="small"
              name="phone_no"
              value={userDetails.phone_no}
              onChange={handleInputChange}
              fullWidth
              margin="dense"
              error={!!formErrors.phone_no}
              helperText={formErrors.phone_no}
              type="tel"
              sx={fieldSx}
            />
          </Grid>
          {!selectedUser && (
            <Grid item xs={12}>
              <CustomTypography>Password</CustomTypography>
              <TextField
                size="small"
                type={showPassword ? "text" : "password"}
                name="password"
                value={userDetails.password}
                onChange={handleInputChange}
                fullWidth
                margin="dense"
                error={!!formErrors.password}
                helperText={formErrors.password}
                sx={fieldSx}
                InputProps={{
                  endAdornment: (
                    <IconButton
                      onClick={() => setShowPassword((prev) => !prev)}
                      edge="end"
                      size="small"
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  ),
                }}
              />
            </Grid>
          )}

          <Grid item xs={12}>
            <CustomTypography>{`Assign Role(s)`}</CustomTypography>
            <Autocomplete
              multiple
              id="roles-autocomplete"
              options={availableRoles}
              getOptionLabel={(option) => option.name || ""}
              value={userDetails.roles || []}
              onChange={handleRolesChange}
              loading={loadingRoles}
              renderInput={(params) => (
                <TextField
                  {...params}
                  size="small"
                  margin="dense"
                  placeholder="Select roles"
                  sx={fieldSx}
                />
              )}
              renderTags={(value, getTagProps) =>
                value.map((option, index) => (
                  <Chip
                    size="small"
                    label={option.name}
                    {...getTagProps({ index })}
                  />
                ))
              }
              isOptionEqualToValue={(option, value) => option._id === value._id}
            />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions style={{ padding: 15 }}>
        <Button
          onClick={() => {
            handleClose();
            initialData();
          }}
          variant="outlined"
          style={{ fontFamily: "Poppins", fontSize: 14, fontWeight: 600 }}
        >
          Cancel
        </Button>
        <Button
          onClick={(e) => (selectedUser ? handleEditData(e) : handleSave(e))}
          variant="contained"
          style={{ fontFamily: "Poppins", fontSize: 14, fontWeight: 600 }}
        >
          {selectedUser ? "Save" : "Add"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default UserModal;
